import React, { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Flex, ModalBody, Button, ModalFooter, ModalHeader, Tag, TagLabel } from "@chakra-ui/react";
import { TextInput } from "@familyzone/component-library";
import { useToast } from "@/components/ui/Toast";
import { ClassroomOverviewDTO, IClassroomStore } from "@/stores/classroomStore_I";
import { CombineClassRequest, CreateCombineClass } from "@/js/api/combineClassesApi";
import useStore from "@/stores/index";
import { getClassroomStore } from "@/stores/classroomStore";
import { FetchStatusError } from "@/js/api/api";
import { colours } from "@/utils/themeUtils";
import { ClasswizeModal } from "@/components/ui/Modal";

type props = {
	open: boolean;
	onClose: () => void;
	selected: ClassroomOverviewDTO[];
	input: string;
	setInput: (input: string) => void;
};

export const SecondStepModal: FC<props> = ({ open, onClose, selected, input, setInput }) => {
	const { t } = useTranslation();
	const { successToast, errorToast } = useToast();
	const [errorMessage, setErrorMessage] = useState("");
	const [isSaving, setIsSaving] = useState(false);

	const onChange = (e: ChangeEvent<HTMLInputElement>) => {
		setInput(e.target.value);
	};

	const sanitisedInput = useMemo(() => input.trim().toLowerCase(), [input]);

	useEffect(() => {
		if (sanitisedInput.length === 0) {
			setErrorMessage("");
			return;
		}

		if (sanitisedInput.length > 50) {
			setErrorMessage(t("overview.combineClassNameMaxLength"));
			return;
		}

		setErrorMessage("");
	}, [sanitisedInput, t]);

	const checkForValidationError = (err: unknown) => {
		if (err instanceof FetchStatusError) {
			if (err.response.status === 400) {
				const text = err.respJson.error as string;
				if (text !== "") {
					setErrorMessage(text.charAt(0).toUpperCase() + text.slice(1));
					return true;
				}
			}
		}
		return false;
	};

	const handleSubmit = async () => {
		const { getTeacherId, getTeacherStableIdV1, addClassroomDataV2, removeClassroomV2 } = getClassroomStore(
			useStore.getState()
		) as IClassroomStore;

		const requestBody: CombineClassRequest = {
			teacherId: getTeacherId(),
			displayName: sanitisedInput,
			combinedClasses: selected.map((classroom) => ({
				id: classroom.stableIdV1,
				type: classroom.type,
			})),
		};

		try {
			setIsSaving(true);

			const { id, memberCount, personalisation, sourceId, sourceType, stableIdV1, type } = await CreateCombineClass(
				getTeacherStableIdV1(),
				requestBody
			);
			// No need to call fetchClassrooms(), we can just add it from response to store
			addClassroomDataV2({
				classroom: {
					canCombine: false,
					id,
					stableIdV1,
					sourceId,
					sourceType,
					memberCount,
					type: type as "CLASSROOM" | "GROUP",
					personalisation,
				},
			});
			selected.forEach((classroom) => {
				removeClassroomV2(classroom.stableIdV1);
			});
			successToast({
				title: t("overview.combineSuccessTitle"),
				description: `${t("overview.combineSuccessDescriptionPrefix")} "${sanitisedInput}"`,
				isClosable: true,
			});
			onClose();
		} catch (err) {
			if (!checkForValidationError(err)) {
				errorToast({
					title: t("overview.combineErrorTitle"),
					description: t("overview.combineErrorDescription"),
					isClosable: true,
				});
				onClose();
			}
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<ClasswizeModal isOpen={open} onClose={onClose} title={t("overview.combineTitle")} size="2xl">
			<ModalHeader>
				<Flex alignItems="center" gap={6}>
					<Flex>
						<Tag variant="info" borderRadius="full" p={2} px={4}>
							<TagLabel color="#051839" fontFamily="Roboto" fontSize="14px">
								{t("overview.combineStepTwoTag")}
							</TagLabel>
						</Tag>
					</Flex>
					<Flex direction="column">
						<Flex>
							<Box color="#051839" fontFamily="Roboto" fontWeight={500} fontSize="16px" lineHeight="20px">
								{t("overview.combineStepTwoBody")}
							</Box>
						</Flex>
						<Flex>
							<Box color={colours.navy[800]} fontFamily="Roboto" fontSize="14px" fontWeight={400} lineHeight="20px">
								{t("overview.combineStepTwoDesc")}
							</Box>
						</Flex>
					</Flex>
				</Flex>
			</ModalHeader>
			<ModalBody>
				<Flex direction="column">
					<Flex>
						<Box color="#051839" fontFamily="Roboto" fontSize="14px" fontWeight={500} lineHeight="20px">
							{t("overview.combineClassNameHeading")}
						</Box>
					</Flex>
					<Flex>
						<Box color={colours.navy[800]} fontFamily="Roboto" fontSize="12px" fontWeight={400} lineHeight="20px">
							{t("overview.combineClassNameDesc")}
						</Box>
					</Flex>
					<Flex mt="4px" width="100%">
						<TextInput
							data-testid="text-input"
							type="text"
							placeholder={t("overview.combineClassNamePlaceholder")}
							width="536px"
							onChange={onChange}
							errorMessage={errorMessage}
						/>
					</Flex>
				</Flex>
			</ModalBody>

			<ModalFooter>
				<Flex direction="row-reverse">
					<Button
						isDisabled={sanitisedInput.length === 0 || !!errorMessage}
						onClick={handleSubmit}
						borderRadius="md"
						data-testid="modal-next-btn"
						isLoading={isSaving}
					>
						{t("overview.combineButton")}
					</Button>
				</Flex>
			</ModalFooter>
		</ClasswizeModal>
	);
};
